// Visual Edit Panel - Design and Content Editor
// Uses react-colorful for color picking, Radix UI for controls

import { useState, useCallback, useEffect } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import * as Tabs from '@radix-ui/react-tabs';
import * as Popover from '@radix-ui/react-popover';
import * as Slider from '@radix-ui/react-slider';
import * as Select from '@radix-ui/react-select';
import { X, Save, RotateCcw, Palette, Type, Box, AlignLeft, Image as ImageIcon, ChevronDown, Check, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ElementInfo } from '@/lib/visualEdit';

interface ElementChanges {
    styles: Partial<CSSStyleDeclaration>;
    content?: string;
    imageSrc?: string;
}

interface VisualEditPanelProps {
    element: ElementInfo;
    onApplyLive: (styles: Partial<CSSStyleDeclaration>) => void;
    onSave: (changes: ElementChanges) => void;
    onCancel: () => void;
    onSelectElement?: (element: ElementInfo) => void;
}

// Label de section mono uppercase (grammaire OFFSET)
function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <h4 className="font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
            {children}
        </h4>
    );
}

// Color picker with popover
function ColorPicker({
    label,
    value,
    onChange
}: {
    label: string;
    value: string;
    onChange: (color: string) => void;
}) {
    const [isOpen, setIsOpen] = useState(false);

    // Parse rgba/rgb to hex
    const parseToHex = (color: string): string => {
        if (color.startsWith('#')) return color;
        if (color.startsWith('rgb')) {
            const match = color.match(/\d+/g);
            if (match && match.length >= 3) {
                const [r, g, b] = match.map(Number);
                return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            }
        }
        return '#000000';
    };

    const hexValue = parseToHex(value);

    return (
        <div className="flex items-center justify-between">
            <span className="text-[12px] text-muted-foreground">{label}</span>
            <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
                <Popover.Trigger asChild>
                    <button className="flex h-8 items-center gap-2 border border-border bg-secondary px-2 transition-colors hover:bg-surface-3">
                        <div
                            className="h-4 w-4 border border-hairline-strong"
                            style={{ backgroundColor: hexValue }}
                        />
                        <span className="font-mono text-[12px] text-foreground">{hexValue}</span>
                    </button>
                </Popover.Trigger>
                <Popover.Portal>
                    <Popover.Content
                        className="z-[9999] border border-hairline-strong bg-card p-3"
                        sideOffset={5}
                    >
                        <HexColorPicker color={hexValue} onChange={onChange} />
                        <div className="mt-2 flex items-center gap-2">
                            <span className="font-mono text-[12px] text-faint">#</span>
                            <HexColorInput
                                color={hexValue}
                                onChange={onChange}
                                className="h-8 flex-1 border border-border bg-secondary px-2 font-mono text-[12px] text-foreground"
                            />
                        </div>
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        </div>
    );
}

// Spacing editor (margin/padding)
function SpacingEditor({
    label,
    value,
    onChange
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
}) {
    // Parse "8px 16px 8px 16px" format
    const parseSpacing = (val: string): [number, number, number, number] => {
        const parts = val.replace(/px/g, '').split(' ').map(Number);
        if (parts.length === 1) return [parts[0], parts[0], parts[0], parts[0]];
        if (parts.length === 2) return [parts[0], parts[1], parts[0], parts[1]];
        if (parts.length === 4) return parts as [number, number, number, number];
        return [0, 0, 0, 0];
    };

    const [top, right, bottom, left] = parseSpacing(value);
    const [linked, setLinked] = useState(top === right && right === bottom && bottom === left);

    const updateValue = (position: 'top' | 'right' | 'bottom' | 'left', newVal: number) => {
        let t = top, r = right, b = bottom, l = left;
        if (linked) {
            t = r = b = l = newVal;
        } else {
            if (position === 'top') t = newVal;
            if (position === 'right') r = newVal;
            if (position === 'bottom') b = newVal;
            if (position === 'left') l = newVal;
        }
        onChange(`${t}px ${r}px ${b}px ${l}px`);
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-[12px] text-muted-foreground">{label}</span>
                <button
                    onClick={() => setLinked(!linked)}
                    className={cn(
                        "border p-1 transition-colors",
                        linked
                            ? "border-foreground text-foreground"
                            : "border-border text-faint hover:text-foreground"
                    )}
                    title={linked ? "Valeurs liées" : "Valeurs indépendantes"}
                >
                    <Link2 className="h-3 w-3" />
                </button>
            </div>
            <div className="grid grid-cols-4 gap-1">
                {(['top', 'right', 'bottom', 'left'] as const).map((pos, idx) => (
                    <input
                        key={pos}
                        type="number"
                        value={[top, right, bottom, left][idx]}
                        onChange={(e) => updateValue(pos, parseInt(e.target.value) || 0)}
                        className="h-8 w-full border border-border bg-secondary px-1 text-center font-mono text-[12px] text-foreground"
                        placeholder={pos[0].toUpperCase()}
                    />
                ))}
            </div>
            <div className="flex justify-between px-2 font-mono text-[10px] text-faint">
                <span>T</span><span>R</span><span>B</span><span>L</span>
            </div>
        </div>
    );
}

// Slider nu partagé (track hairline, thumb carré encre)
function TokenSlider({
    value,
    onChange,
    min,
    max,
}: {
    value: number;
    onChange: (v: number) => void;
    min: number;
    max: number;
}) {
    return (
        <Slider.Root
            value={[value]}
            onValueChange={([v]) => onChange(v)}
            min={min}
            max={max}
            step={1}
            className="relative flex h-5 w-full touch-none select-none items-center"
        >
            <Slider.Track className="relative h-px grow bg-hairline-strong">
                <Slider.Range className="absolute h-full bg-foreground" />
            </Slider.Track>
            <Slider.Thumb className="block h-3 w-3 bg-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" />
        </Slider.Root>
    );
}

// Font size selector
function FontSizeEditor({
    value,
    onChange
}: {
    value: string;
    onChange: (value: string) => void;
}) {
    const size = parseInt(value) || 16;
    const presets = [
        { label: 'xs', value: 12 },
        { label: 'sm', value: 14 },
        { label: 'md', value: 16 },
        { label: 'lg', value: 18 },
        { label: 'xl', value: 20 },
        { label: '2xl', value: 24 },
        { label: '3xl', value: 30 },
    ];

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-[12px] text-muted-foreground">Taille police</span>
                <span className="font-mono text-[12px] text-foreground">{size}px</span>
            </div>
            <TokenSlider value={size} onChange={(v) => onChange(`${v}px`)} min={10} max={48} />
            <div className="flex flex-wrap gap-1">
                {presets.map(p => (
                    <button
                        key={p.label}
                        onClick={() => onChange(`${p.value}px`)}
                        className={cn(
                            "px-2 py-0.5 font-mono text-[11px] transition-colors",
                            size === p.value
                                ? "bg-foreground text-background"
                                : "bg-secondary text-muted-foreground hover:text-foreground"
                        )}
                    >
                        {p.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// Font weight selector
function FontWeightEditor({
    value,
    onChange
}: {
    value: string;
    onChange: (value: string) => void;
}) {
    const weights = ['100', '200', '300', '400', '500', '600', '700', '800', '900'];
    const currentWeight = value || '400';

    return (
        <div className="flex items-center justify-between">
            <span className="text-[12px] text-muted-foreground">Graisse</span>
            <Select.Root value={currentWeight} onValueChange={onChange}>
                <Select.Trigger className="flex h-8 items-center gap-2 border border-border bg-secondary px-3 font-mono text-[12px] text-foreground transition-colors hover:bg-surface-3">
                    <Select.Value />
                    <Select.Icon>
                        <ChevronDown className="h-3 w-3 text-faint" />
                    </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                    <Select.Content className="z-[9999] overflow-hidden border border-hairline-strong bg-card">
                        <Select.Viewport className="p-1">
                            {weights.map(w => (
                                <Select.Item
                                    key={w}
                                    value={w}
                                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 font-mono text-[12px] text-muted-foreground outline-none hover:bg-secondary data-[highlighted]:bg-secondary data-[highlighted]:text-foreground"
                                >
                                    <Select.ItemIndicator className="w-4">
                                        <Check className="h-3 w-3 text-foreground" />
                                    </Select.ItemIndicator>
                                    <Select.ItemText style={{ fontWeight: w }}>{w}</Select.ItemText>
                                </Select.Item>
                            ))}
                        </Select.Viewport>
                    </Select.Content>
                </Select.Portal>
            </Select.Root>
        </div>
    );
}

// Border radius editor
function BorderRadiusEditor({
    value,
    onChange
}: {
    value: string;
    onChange: (value: string) => void;
}) {
    const radius = parseInt(value) || 0;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-[12px] text-muted-foreground">Coins arrondis</span>
                <span className="font-mono text-[12px] text-foreground">{radius}px</span>
            </div>
            <TokenSlider value={radius} onChange={(v) => onChange(`${v}px`)} min={0} max={32} />
        </div>
    );
}

// Main Visual Edit Panel
export function VisualEditPanel({
    element,
    onApplyLive,
    onSave,
    onCancel,
    onSelectElement
}: VisualEditPanelProps) {
    // Initialize state from element's computed styles
    const [styles, setStyles] = useState<Partial<CSSStyleDeclaration>>({
        backgroundColor: element.computedStyles?.backgroundColor || 'transparent',
        color: element.computedStyles?.color || '#000000',
        padding: element.computedStyles?.padding || '0px',
        margin: element.computedStyles?.margin || '0px',
        fontSize: element.computedStyles?.fontSize || '16px',
        fontWeight: element.computedStyles?.fontWeight || '400',
        borderRadius: element.computedStyles?.borderRadius || '0px',
    });

    const [content, setContent] = useState(element.textContent || '');
    const [imageSrc, setImageSrc] = useState(element.imageSrc || '');

    // Apply live changes when styles update
    const updateStyle = useCallback((key: keyof CSSStyleDeclaration, value: string) => {
        setStyles(prev => {
            const updated = { ...prev, [key]: value };
            onApplyLive(updated);
            return updated;
        });
    }, [onApplyLive]);

    const handleSave = () => {
        onSave({
            styles,
            content: element.tagName !== 'IMG' ? content : undefined,
            imageSrc: element.tagName === 'IMG' ? imageSrc : undefined
        });
    };

    const handleReset = () => {
        setStyles({
            backgroundColor: element.computedStyles?.backgroundColor || 'transparent',
            color: element.computedStyles?.color || '#000000',
            padding: element.computedStyles?.padding || '0px',
            margin: element.computedStyles?.margin || '0px',
            fontSize: element.computedStyles?.fontSize || '16px',
            fontWeight: element.computedStyles?.fontWeight || '400',
            borderRadius: element.computedStyles?.borderRadius || '0px',
        });
        setContent(element.textContent || '');
        setImageSrc(element.imageSrc || '');
        onApplyLive({});
    };

    const isImageElement = element.tagName === 'IMG';

    return (
        <div className="flex h-full flex-col border-l border-border bg-card">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center border border-border bg-secondary">
                        <Box className="h-3 w-3 text-foreground" />
                    </div>
                    <div>
                        <h3 className="text-[13px] font-medium text-foreground">Édition visuelle</h3>
                        <p className="font-mono text-[11px] text-faint">
                            {element.tagName.toLowerCase()}
                            {element.className && `.${element.className.split(' ')[0]}`}
                        </p>
                    </div>
                </div>
                <button
                    onClick={onCancel}
                    className="p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                    aria-label="Fermer"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            {/* Tabs */}
            <Tabs.Root defaultValue="design" className="flex flex-1 flex-col overflow-hidden">
                <Tabs.List className="flex border-b border-border">
                    <Tabs.Trigger
                        value="design"
                        className="-mb-px flex flex-1 items-center justify-center gap-2 border-b border-transparent px-4 py-2.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground data-[state=active]:border-foreground data-[state=active]:text-foreground"
                    >
                        <Palette className="h-4 w-4" />
                        Design
                    </Tabs.Trigger>
                    <Tabs.Trigger
                        value="content"
                        className="-mb-px flex flex-1 items-center justify-center gap-2 border-b border-transparent px-4 py-2.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground data-[state=active]:border-foreground data-[state=active]:text-foreground"
                    >
                        {isImageElement ? <ImageIcon className="h-4 w-4" /> : <Type className="h-4 w-4" />}
                        Contenu
                    </Tabs.Trigger>
                </Tabs.List>

                {/* Design Tab */}
                <Tabs.Content value="design" className="flex-1 space-y-5 overflow-y-auto p-4">
                    <div className="space-y-4">
                        <SectionLabel>Couleurs</SectionLabel>
                        <ColorPicker
                            label="Arrière-plan"
                            value={styles.backgroundColor as string}
                            onChange={(c) => updateStyle('backgroundColor', c)}
                        />
                        <ColorPicker
                            label="Texte"
                            value={styles.color as string}
                            onChange={(c) => updateStyle('color', c)}
                        />
                    </div>

                    <div className="h-px bg-border" />

                    <div className="space-y-4">
                        <SectionLabel>Espacement</SectionLabel>
                        <SpacingEditor
                            label="Padding"
                            value={styles.padding as string}
                            onChange={(v) => updateStyle('padding', v)}
                        />
                        <SpacingEditor
                            label="Margin"
                            value={styles.margin as string}
                            onChange={(v) => updateStyle('margin', v)}
                        />
                    </div>

                    <div className="h-px bg-border" />

                    <div className="space-y-4">
                        <SectionLabel>Typographie</SectionLabel>
                        <FontSizeEditor
                            value={styles.fontSize as string}
                            onChange={(v) => updateStyle('fontSize', v)}
                        />
                        <FontWeightEditor
                            value={styles.fontWeight as string}
                            onChange={(v) => updateStyle('fontWeight', v)}
                        />
                    </div>

                    <div className="h-px bg-border" />

                    <div className="space-y-4">
                        <SectionLabel>Bordures</SectionLabel>
                        <BorderRadiusEditor
                            value={styles.borderRadius as string}
                            onChange={(v) => updateStyle('borderRadius', v)}
                        />
                    </div>
                </Tabs.Content>

                {/* Content Tab */}
                <Tabs.Content value="content" className="flex-1 space-y-4 overflow-y-auto p-4">
                    {/* Children Drill-down - Show first if element has children */}
                    {element.children && element.children.length > 0 && (
                        <div className="mb-6 space-y-3 border-b border-border pb-6">
                            <SectionLabel>Structure</SectionLabel>
                            <p className="mb-2 text-[12px] text-muted-foreground">
                                Cet élément contient {element.children.length} sous-éléments.
                                Cliquez sur un élément pour l'éditer spécifiquement.
                            </p>
                            <div className="space-y-1">
                                {element.children.map((child, i) => (
                                    <button
                                        key={i}
                                        onClick={() => onSelectElement && onSelectElement({ ...child } as any)}
                                        className="group flex w-full items-center justify-between border border-border bg-secondary px-3 py-2 text-left transition-colors hover:bg-surface-3"
                                    >
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <span className="shrink-0 font-mono text-[11px] text-foreground">
                                                {child.tagName.toLowerCase()}
                                                {child.className && `.${child.className.split(' ')[0]}`}
                                            </span>
                                            {child.textContent && (
                                                <span className="truncate text-[12px] text-muted-foreground">
                                                    {child.textContent}
                                                </span>
                                            )}
                                        </div>
                                        <ChevronDown className="h-3 w-3 -rotate-90 text-faint transition-colors group-hover:text-foreground" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {isImageElement ? (
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-[12px] text-muted-foreground">
                                <ImageIcon className="h-4 w-4" />
                                URL de l'image
                            </label>
                            <input
                                type="url"
                                value={imageSrc}
                                onChange={(e) => setImageSrc(e.target.value)}
                                placeholder="https://…"
                                className="h-8 w-full border border-border bg-secondary px-3 font-mono text-[12px] text-foreground placeholder:text-faint"
                            />
                            {imageSrc && (
                                <div className="mt-2 overflow-hidden border border-border">
                                    <img src={imageSrc} alt="Préview" className="h-auto w-full" />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {element.children && element.children.length > 0 ? (
                                <div className="border border-border bg-secondary p-3 text-center text-[12px] text-muted-foreground">
                                    Sélectionnez un sous-élément ci-dessus pour modifier son contenu textuel.
                                </div>
                            ) : (
                                <>
                                    <label className="flex items-center gap-2 text-[12px] text-muted-foreground">
                                        <AlignLeft className="h-4 w-4" />
                                        Contenu texte direct
                                    </label>
                                    <textarea
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        rows={6}
                                        className="w-full resize-none border border-border bg-secondary px-3 py-2 text-[13px] text-foreground placeholder:text-faint"
                                        placeholder="Entrez le texte…"
                                    />
                                    <p className="font-mono text-[11px] text-faint">
                                        {content.length} caractères
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </Tabs.Content>
            </Tabs.Root>

            {/* Footer Actions */}
            <div className="flex items-center gap-2 border-t border-border px-4 py-3">
                <button
                    onClick={handleReset}
                    className="flex flex-1 items-center justify-center gap-2 border border-border bg-card px-4 py-2 text-[13px] text-foreground transition-colors hover:bg-secondary"
                >
                    <RotateCcw className="h-4 w-4" />
                    Réinitialiser
                </button>
                <button
                    onClick={handleSave}
                    className="flex flex-1 items-center justify-center gap-2 bg-primary px-4 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-foreground"
                >
                    <Save className="h-4 w-4" />
                    Sauvegarder
                </button>
            </div>
        </div>
    );
}

export default VisualEditPanel;
